import React, { useState } from "react";
import {
  Layout,
  Card,
  Space,
  Row,
  Col,
  DatePicker,
  Button,
  Table,
  Statistic,
  Typography,
  message,
  Select,
} from "antd";
import {
  FilterOutlined,
  DollarOutlined,
  FieldTimeOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { getPaymentsStatistics } from "../../../api/statistics/statisticsRequest";
import Heatmap from "highcharts/modules/heatmap";
import { formatAmountInFrench } from "../../../utils/constants";

const { Content } = Layout;
const { RangePicker } = DatePicker;
const { Title } = Typography;

const { Option } = Select;

Heatmap(Highcharts);

const PaymentsStatistics = () => {
  const [dateRange, setDateRange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentsData, setPaymentsData] = useState(null);

  const [selectedGroupBy, setSelectedGroupBy] = useState(null);

  const fetchPaymentsData = async () => {
    setLoading(true);
    try {
      const data = await getPaymentsStatistics({
        start_date:
          dateRange && dateRange[0]
            ? dateRange[0].format("YYYY-MM-DD HH:mm:ss")
            : null,
        end_date:
          dateRange && dateRange[1]
            ? dateRange[1].format("YYYY-MM-DD HH:mm:ss")
            : null,
        group_by: selectedGroupBy,
      });
      setPaymentsData(data); // Update state with the fetched data
    } catch (error) {
      // Handle errors here
      setPaymentsData(null);
      message.error("Échec de la récupération des données");
    } finally {
      setLoading(false);
    }
  };

  const filterPaymentsStatistics = () => {
    // Implement filtering logic here
    console.log("Filtrage avec la plage de dates :", dateRange);
    fetchPaymentsData(); // This should be replaced with an actual API call using the date range
  };

  Highcharts.setOptions({
    lang: {
      months: [
        "Janvier",
        "Février",
        "Mars",
        "Avril",
        "Mai",
        "Juin",
        "Juillet",
        "Août",
        "Septembre",
        "Octobre",
        "Novembre",
        "Décembre",
      ],
      weekdays: [
        "Dimanche",
        "Lundi",
        "Mardi",
        "Mercredi",
        "Jeudi",
        "Vendredi",
        "Samedi",
      ],
      shortMonths: [
        "Jan",
        "Fév",
        "Mar",
        "Avr",
        "Mai",
        "Juin",
        "Juil",
        "Aoû",
        "Sep",
        "Oct",
        "Nov",
        "Déc",
      ],
    },
  });

  const timelineChartOptions = {
    chart: {
      zoomType: "x",
    },
    title: {
      text: "Chronologie des paiements",
    },
    xAxis: {
      type: "datetime",
      dateTimeLabelFormats: {
        day: "%e %b",
      },
    },
    credits: {
      enabled: false,
    },
    yAxis: [
      {
        title: {
          text: "Nombre de paiements",
        },
      },
      {
        title: {
          text: "Montant",
        },
        opposite: true,
      },
    ],
    tooltip: {
      shared: true,
    },
    series: [
      {
        name: "Nombre de paiements",
        type: "column",
        data: paymentsData
          ? paymentsData.timeline.map((item) => [
              new Date(item.date).getTime(),
              item.count,
            ])
          : [],
      },
      {
        name: "Montant total",
        type: "spline",
        yAxis: 1,
        data: paymentsData
          ? paymentsData.timeline.map((item) => [
              new Date(item.date).getTime(),
              parseFloat(item.total_amount),
            ])
          : [],
      },
    ],
  };
  const hourlyChartOptions = {
    chart: {
      type: "column",
    },
    title: {
      text: "Répartition horaire des paiements",
    },
    xAxis: {
      categories: paymentsData
        ? Object.keys(paymentsData.hourly_distribution).map(
            (hour) => `${hour}:00`
          )
        : [],
      title: {
        text: "Heure de la journée",
      },
    },
    credits: {
      enabled: false,
    },
    yAxis: [
      {
        min: 0,
        title: {
          text: "Nombre de paiements",
        },
      },
      {
        min: 0,
        title: {
          text: "Montant",
        },
        opposite: true,
      },
    ],
    tooltip: {
      shared: true,
    },
    series: [
      {
        name: "Total des paiements",
        type: "column",
        data: paymentsData
          ? Object.values(paymentsData.hourly_distribution).map(
              (item) => item.count
            )
          : [],
      },
      {
        name: "Montant total",
        type: "spline",
        yAxis: 1,
        data: paymentsData
          ? Object.values(paymentsData.hourly_distribution).map((item) =>
              parseFloat(item.total_amount)
            )
          : [],
      },
    ],
  };
  const heatmapData = paymentsData
    ? Object.entries(paymentsData.hourly_distribution).map(([hour, data]) => [
        parseInt(hour),
        0,
        data.count,
      ])
    : [];

  const heatmapChartOptions = {
    chart: {
      type: "heatmap",
    },
    title: {
      text: "Carte thermique des paiements",
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories: [
        "00:00",
        "01:00",
        "02:00",
        "03:00",
        "04:00",
        "05:00",
        "06:00",
        "07:00",
        "08:00",
        "09:00",
        "10:00",
        "11:00",
        "12:00",
        "13:00",
        "14:00",
        "15:00",
        "16:00",
        "17:00",
        "18:00",
        "19:00",
        "20:00",
        "21:00",
        "22:00",
        "23:00",
      ],
    },
    yAxis: {
      categories: [""],
      title: null,
    },
    colorAxis: {
      min: 0,
      minColor: "#FFFFFF",
      maxColor: Highcharts.getOptions().colors[0],
    },
    legend: {
      align: "right",
      layout: "vertical",
      margin: 0,
      verticalAlign: "top",
      y: 25,
      symbolHeight: 280,
    },
    tooltip: {
      formatter: function () {
        return (
          "<b>" +
          this.series.xAxis.categories[this.point.x] +
          "</b> avait <br><b>" +
          this.point.value +
          "</b> paiements"
        );
      },
    },
    series: [
      {
        name: "Paiements par heure",
        borderWidth: 1,
        type: "heatmap",
        data: heatmapData,
      },
    ],
  };

  const typeBreakdownChartOptions = {
    chart: {
      type: "pie",
    },
    title: {
      text: "Répartition par Type de Paiement",
    },
    credits: {
      enabled: false,
    },
    tooltip: {
      pointFormat:
        "{series.name}: <b>{point.percentage:.1f}%</b><br/>Montant: <b>{point.y} MAD</b><br/>Nombre: <b>{point.count}</b>",
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          format: "<b>{point.name}</b>: {point.percentage:.1f} %",
        },
      },
    },
    series: [
      {
        name: "Type de Paiement",
        colorByPoint: true,
        data: paymentsData?.by_type_summary
          ? [
              {
                name: "Infractions",
                y: parseFloat(
                  paymentsData.by_type_summary.infraction.total_amount
                ),
                count: paymentsData.by_type_summary.infraction.count,
                color: "#ff4d4f",
              },
              {
                name: "Abonnements",
                y: parseFloat(
                  paymentsData.by_type_summary.subscription.total_amount
                ),
                count: paymentsData.by_type_summary.subscription.count,
                color: "#52c41a",
              },
            ]
          : [],
      },
    ],
  };

  return (
    <Layout className="min-h-screen">
      <Content className="py-6 sm:px-6 lg:px-8">
        <Card className="mb-6 shadow-sm">
          <Space direction="vertical" size="middle" className="w-full">
            <Row gutter={16} align="middle">
              <Col xs={24} sm={8}>
                <RangePicker
                  onChange={(dates) => setDateRange(dates)}
                  className="w-full"
                  showTime={{ format: "HH:mm" }}
                />
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Select
                  placeholder="Grouper par"
                  style={{ width: "100%" }}
                  onChange={(value) => setSelectedGroupBy(value)}
                  allowClear
                >
                  <Option value={"daily"}>Quotidien</Option>
                  <Option value={"monthly"}>Mensuel</Option>
                  <Option value={"yearly"}>Annuel</Option>
                </Select>
              </Col>
            </Row>
            <Row>
              <Col xs={24} sm={4}>
                <Button
                  type="primary"
                  icon={<FilterOutlined />}
                  onClick={filterPaymentsStatistics}
                  loading={loading}
                  className="w-full"
                >
                  Rechercher
                </Button>
              </Col>
            </Row>
          </Space>
        </Card>

        {paymentsData && (
          <Space direction="vertical" size="large" className="w-full">
            {" "}
            <Card className="shadow-sm">
              <Title level={2} className="text-center mb-6">
                Résumé des paiements
              </Title>
              <Row gutter={16} className="mb-6">
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Total des paiements"
                    value={paymentsData.summary.total_payments}
                    prefix={<FieldTimeOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Montant total"
                    value={formatAmountInFrench(
                      parseFloat(paymentsData.summary.total_amount).toFixed(2)
                    )}
                    prefix={<DollarOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Montant moyen"
                    value={formatAmountInFrench(
                      parseFloat(paymentsData.summary.average_amount).toFixed(2)
                    )}
                    prefix={<DollarOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Paiement maximum"
                    value={formatAmountInFrench(
                      parseFloat(paymentsData.summary.max_payment).toFixed(2)
                    )}
                    prefix={<ArrowUpOutlined />}
                    valueStyle={{ color: "#3f8600" }}
                  />
                </Col>
              </Row>

              {/* Payment Types Breakdown */}
              <Row gutter={16} className="mb-6">
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Paiements Infractions"
                    value={paymentsData.by_type_summary?.infraction?.count || 0}
                    prefix={<ArrowDownOutlined />}
                    valueStyle={{ color: "#ff4d4f" }}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Montant Infractions"
                    value={formatAmountInFrench(
                      parseFloat(
                        paymentsData.by_type_summary?.infraction
                          ?.total_amount || 0
                      ).toFixed(2)
                    )}
                    prefix={<DollarOutlined />}
                    valueStyle={{ color: "#ff4d4f" }}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Paiements Abonnements"
                    value={
                      paymentsData.by_type_summary?.subscription?.count || 0
                    }
                    prefix={<ArrowUpOutlined />}
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Montant Abonnements"
                    value={formatAmountInFrench(
                      parseFloat(
                        paymentsData.by_type_summary?.subscription
                          ?.total_amount || 0
                      ).toFixed(2)
                    )}
                    prefix={<DollarOutlined />}
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Col>
              </Row>
            </Card>{" "}
            <Card className="shadow-sm">
              <HighchartsReact
                highcharts={Highcharts}
                options={timelineChartOptions}
              />
            </Card>
            <Card className="shadow-sm">
              <HighchartsReact
                highcharts={Highcharts}
                options={typeBreakdownChartOptions}
              />
            </Card>
            <Card className="shadow-sm">
              <HighchartsReact
                highcharts={Highcharts}
                options={hourlyChartOptions}
              />
            </Card>
            <Card className="shadow-sm">
              <HighchartsReact
                highcharts={Highcharts}
                options={heatmapChartOptions}
              />
            </Card>
            {/* <Card className="shadow-sm">
              <Title level={3} className="mb-4">
                Répartition horaire des paiements
              </Title>
              <Table
                dataSource={paymentsData.hourly_distribution}
                columns={[
                  {
                    title: "Heure",
                    dataIndex: "hour",
                    key: "hour",
                    render: (text) => `${text}:00`,
                  },
                  {
                    title: "Nombre agents",
                    dataIndex: "unique_agents",
                    key: "unique_agents",
                    align: "center",
                    sorter: (a, b) => a.unique_agents - b.unique_agents,
                  },
                  {
                    title: "Total des paiements",
                    dataIndex: "total_payments",
                    key: "total_payments",
                    align: "center",
                    sorter: (a, b) => a.total_payments - b.total_payments,
                  },
                  {
                    title: "Montant moyen",
                    dataIndex: "average_amount",
                    key: "average_amount",
                    align: "right",
                    sorter: (a, b) =>
                      parseFloat(a.average_amount) -
                      parseFloat(b.average_amount),
                    render: (text) =>
                      `${formatAmountInFrench(parseFloat(text).toFixed(2))}`,
                  },
                  {
                    title: "Montant total",
                    dataIndex: "total_amount",
                    key: "total_amount",
                    align: "right",
                    sorter: (a, b) =>
                      parseFloat(a.total_amount) - parseFloat(b.total_amount),
                    render: (text) =>
                      `${formatAmountInFrench(parseFloat(text).toFixed(2))}`,
                  },
                ]}
                pagination={false}
              />
            </Card> */}{" "}
            {/* <Card className="shadow-sm mt-6">
              <Title level={3} className="mb-4">
                Détails des Statistiques par Contrat
              </Title>
              <Table
                dataSource={paymentsData?.by_zone}
                columns={[
                  { title: "Zone", dataIndex: "zone_name", key: "zone_name" },
                  {
                    title: "Nombre de Paiements",
                    dataIndex: "count",
                    key: "count",
                    align: "center",
                    sorter: (a, b) => a.count - b.count,
                  },
                  {
                    title: "Montant Moyen",
                    dataIndex: "average_amount",
                    key: "average_amount",
                    align: "right",
                    sorter: (a, b) =>
                      parseFloat(a.average_amount) -
                      parseFloat(b.average_amount),
                    render: (text) =>
                      formatAmountInFrench(parseFloat(text).toFixed(2)),
                  },
                  {
                    title: "Montant Total",
                    dataIndex: "total_amount",
                    key: "total_amount",
                    align: "right",
                    sorter: (a, b) =>
                      parseFloat(a.total_amount) - parseFloat(b.total_amount),
                    render: (text) =>
                      formatAmountInFrench(parseFloat(text).toFixed(2)),
                  },
                ]}
                pagination={false}
                rowKey="zone_id"
              />
            </Card> */}
            <Card className="shadow-sm mt-6">
              <Title level={3} className="mb-4">
                Détails par Type de Paiement
              </Title>
              <Table
                dataSource={
                  paymentsData?.by_type_summary
                    ? [
                        {
                          key: "infraction",
                          type: "Infractions",
                          count: paymentsData.by_type_summary.infraction.count,
                          total_amount:
                            paymentsData.by_type_summary.infraction
                              .total_amount,
                          average_amount:
                            paymentsData.by_type_summary.infraction
                              .average_amount,
                        },
                        {
                          key: "subscription",
                          type: "Abonnements",
                          count:
                            paymentsData.by_type_summary.subscription.count,
                          total_amount:
                            paymentsData.by_type_summary.subscription
                              .total_amount,
                          average_amount:
                            paymentsData.by_type_summary.subscription
                              .average_amount,
                        },
                      ]
                    : []
                }
                columns={[
                  {
                    title: "Type de Paiement",
                    dataIndex: "type",
                    key: "type",
                    render: (text, record) => (
                      <span
                        style={{
                          color:
                            record.key === "infraction" ? "#ff4d4f" : "#52c41a",
                          fontWeight: "bold",
                        }}
                      >
                        {text}
                      </span>
                    ),
                  },
                  {
                    title: "Nombre de Paiements",
                    dataIndex: "count",
                    key: "count",
                    align: "center",
                    sorter: (a, b) => a.count - b.count,
                  },
                  {
                    title: "Montant Moyen",
                    dataIndex: "average_amount",
                    key: "average_amount",
                    align: "right",
                    sorter: (a, b) => a.average_amount - b.average_amount,
                    render: (text) =>
                      `${formatAmountInFrench(
                        parseFloat(text).toFixed(2)
                      )}`,
                  },
                  {
                    title: "Montant Total",
                    dataIndex: "total_amount",
                    key: "total_amount",
                    align: "right",
                    sorter: (a, b) => a.total_amount - b.total_amount,
                    render: (text) =>
                      `${formatAmountInFrench(
                        parseFloat(text).toFixed(2)
                      )}`,
                  },
                ]}
                pagination={false}
                rowKey="key"
              />
            </Card>
          </Space>
        )}
      </Content>
    </Layout>
  );
};

export default PaymentsStatistics;
